import { getSafeInnerHTMLProp } from '@/helpers/getSafeInnerHTML';
import Accordion from '../Accordion/Accordion';
import BenefitsAppTeaser from '../BenefitsAppTeaser/BenefitsAppTeaser';
import BenefitsCategory from '../BenefitsCategory/BenefitsCategory';
import BenefitsDealCard from '../BenefitsDealCard/BenefitsDealCard';
import BenefitsOverviewAppBanner from '../BenefitsOverviewAppBanner/BenefitsOverviewAppBanner';
import BenefitsOverviewHero from '../BenefitsOverviewHero/BenefitsOverviewHero';
import BenefitsSuggestionCard from '../BenefitsSuggestionCard/BenefitsSuggestionCard';
import Container from '../Container/Container';
import { HeadlineAlign, HeadlineLevel } from '../Headline/consts';
import Headline from '../Headline/Headline';
import NotAvailableInfo from '../NotAvailableInfo/NotAvailableInfo';
import Section from '../Section/Section';
import TwoColumnContainer from '../TwoColumnContainer/TwoColumnContainer';
import styles from './BenefitsOverviewPageContent.module.scss';
import {
    NOT_AVAILABLE_INFO_DESCRIPTION, NOT_AVAILABLE_INFO_TITLE, SOON_AVAILABLE_ICON
} from './consts';

import type {
  BenefitsCategory as BenefitsCategoryProps,
  BenefitsOverviewPageContentProps,
} from './types';

const addComponentAtPosition = (
  component: React.ReactNode,
  components: React.ReactNode[],
  placement: 'start' | 'middle' | 'end'
) => {
  if (placement === 'start') {
    return [component, ...components];
  }

  if (placement === 'middle') {
    const middleIndex = Math.floor(components.length / 2);
    return [
      ...components.slice(0, middleIndex),
      component,
      ...components.slice(middleIndex),
    ];
  }

  return [...components, component];
};

const renderDealsForCategory = (category: BenefitsCategoryProps) => {
  let components: React.ReactNode[] = category.deals.map((deal) => {
    return (
      <BenefitsDealCard
        key={deal.id}
        logo={deal.logo}
        image={deal.image}
        headline={deal.headline}
        link={deal.link}
        badgeText={deal.badgeText}
        variant={deal.status === 'inactive' ? 'noCouponsAvailable' : undefined}
      />
    );
  });

  if (category.showAppTeaser) {
    components = addComponentAtPosition(
      <BenefitsAppTeaser key="BenefitsAppTeaser" />,
      components,
      category.appTeaserPlacement
    );
  }

  if (category.showSuggestionTeaser) {
    components = addComponentAtPosition(
      <BenefitsSuggestionCard key="BenefitsSuggestionCard" />,
      components,
      category.suggestionTeaserPlacement
    );
  }

  return components;
};

const BenefitsOverviewPageContent = ({
  categories,
  dealsAvailableSoon,
  faqItems,
  footNote,
  headline,
  image,
  showAppBanner = false,
  stageVariant,
  subHeadline,
  vimeoUrl,
}: BenefitsOverviewPageContentProps) => {
  if (!categories.length) {
    return (
      <Section>
        <Container>
          <NotAvailableInfo
            title={NOT_AVAILABLE_INFO_TITLE}
            text={NOT_AVAILABLE_INFO_DESCRIPTION}
            icon="user/rocket"
          />
        </Container>
      </Section>
    );
  }

  return (
    <div className={styles.base}>
      <BenefitsOverviewHero
        image={image}
        headline={headline}
        description={subHeadline}
        buttonText="Vorteile entdecken"
        vimeoUrl={vimeoUrl}
        stageVariant={stageVariant}
      />

      {showAppBanner && <BenefitsOverviewAppBanner />}

      <Section sectionId="vorteile">
        {categories.map((category) => {
          return (
            <BenefitsCategory
              color={category.color}
              icon={category.icon}
              id={category.sort}
              key={category.id}
              title={category.title}
            >
              {renderDealsForCategory(category)}
            </BenefitsCategory>
          );
        })}
        {!!dealsAvailableSoon.length && (
          <BenefitsCategory
            color="blue"
            icon={SOON_AVAILABLE_ICON}
            id={0}
            title="<strong>Bald</strong> <em>verfügbar</em>"
          >
            {dealsAvailableSoon.map((deal) => (
              <BenefitsDealCard
                key={deal.id}
                logo={deal.logo}
                image={deal.image}
                headline={deal.headline}
                link={deal.link}
                variant="availableSoon"
              />
            ))}
          </BenefitsCategory>
        )}
      </Section>
      <Section>
        <TwoColumnContainer
          leftSideWidth={40}
          contents-left={
            <Headline
              level={HeadlineLevel.Three}
              subText="Häufig gestellte Fragen zu unseren<br/> Gutscheinen"
              align={HeadlineAlign.Left}
              headlineColor="gradient"
            >
              Gut zu wissen
            </Headline>
          }
          contents-right={<Accordion items={faqItems} />}
        />
      </Section>
      <Section>
        <Container>
          <div
            className={styles.footnote}
            {...getSafeInnerHTMLProp(footNote)}
          />
        </Container>
      </Section>
    </div>
  );
};

export default BenefitsOverviewPageContent;
